import DataTable, {AjaxData, Order} from "datatables.net-bs5";
import * as Highcharts from "highcharts";
import {get, init_tooltips, notifier, number_format, on, val} from "./util";
import { getChartTheme } from './utils/chartTheme';

interface BooksAjaxData extends AjaxData {
    books_id : string
}

declare var books_monthly: Array<{ month: string; count: number }>;
declare var books_monthly_pace: Array<{ month: string; chapters: number }>;
declare var books_monthly_time: Array<{ month: string; minutes: number }>;
declare var books_monthly_chapters: Array<{ month: string; count: number }>;

export default class {
    static init = () => {
        books();
        reads();
        stats();
    }
}

// ── Stats page ──────────────────────────────────────────────────────────────

function renderSparkline(id: string, data: number[]): void {
    const el = document.getElementById(id);
    if (!el) return;
    if (!data || data.length === 0) return;

    Highcharts.chart(id, Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'area',
            height: 30,
            margin: [2, 0, 2, 0],
            animation: false,
        },
        title: { text: undefined },
        credits: { enabled: false },
        legend: { enabled: false },
        tooltip: { enabled: false },
        xAxis: {
            visible: false,
        },
        yAxis: {
            visible: false,
            startOnTick: false,
            endOnTick: false,
        },
        plotOptions: {
            area: {
                lineWidth: 1,
                color: '#4dabf7',
                fillOpacity: 0.15,
                marker: { enabled: false },
                animation: false,
            },
        },
        series: [{
            type: 'area',
            data: data,
        }],
    } as any));
}

function renderEmptyState(id: string, height: number, message: string = 'Not enough data to display.'): void {
    const el = document.getElementById(id);
    if (!el) return;
    el.style.height = height + 'px';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.innerHTML = `<p class="text-center text-muted mb-0">${message}</p>`;
}

function renderBadge(selector: string): void {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) return;
    const raw = el.getAttribute('data-change');
    if (raw === null || raw === '') return;

    const value = parseFloat(raw);
    if (isNaN(value)) return;

    const isTime = selector.includes('time');
    let label: string;
    let cls: string;

    if (isTime) {
        const absMinutes = Math.abs(Math.round(value / 60));
        const h = Math.floor(absMinutes / 60);
        const m = absMinutes % 60;
        const parts: string[] = [];
        if (h > 0) parts.push(h + 'h');
        if (m > 0) parts.push(m + 'm');
        const formatted = parts.length > 0 ? parts.join(' ') : '0m';
        label = (value >= 0 ? '+' : '-') + formatted;
        cls = value >= 0 ? 'bg-success' : 'bg-danger';
    } else {
        label = (value >= 0 ? '+' : '') + value;
        cls = value >= 0 ? 'bg-success' : 'bg-danger';
    }

    el.innerHTML = `<span class="badge ${cls}">${label}</span>`;
}

function wireRangeButtons(
    selector: string,
    allData: Array<Record<string, any>>,
    chart: Highcharts.Chart,
    dateField: string,
    yField: string
): void {
    const group = document.querySelector(selector);
    if (!group) return;

    group.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
            // Update active state
            group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            const range = btn.getAttribute('data-range');
            let filtered = allData;

            if (range === '6M' || range === '1Y') {
                const months = range === '6M' ? 6 : 12;
                const cutoff = new Date();
                cutoff.setMonth(cutoff.getMonth() - months);
                filtered = allData.filter((d: Record<string, any>) => new Date(d[dateField]) >= cutoff);
            }

            chart.xAxis[0].setCategories(filtered.map((d: Record<string, any>) => d[dateField]));
            chart.series[0].setData(filtered.map((d: Record<string, any>) => d[yField]));
        });
    });
}

function stats(): void {
    // If stats.latte rendered the empty state (total_books_completed === 0),
    // none of the card containers exist in DOM — bail early to avoid noise.
    if (typeof books_monthly === 'undefined') return;

    // ── Sparklines (each has its own length guard inside renderSparkline) ───

    // Books sparkline — last 12 months, completion-based (unchanged from current behavior)
    const last12Books = books_monthly.slice(-12);
    renderSparkline('sparkline_books', last12Books.map(d => d.count));

    // Chapters sparkline — real chapter totals, all-time, session-based
    if (typeof books_monthly_chapters !== 'undefined' && books_monthly_chapters.length > 0) {
        renderSparkline('sparkline_chapters', books_monthly_chapters.map(d => d.count));
    }
    // (else: sparkline_chapters container stays empty per CONTEXT.md locked decision)

    // Time sparkline — monthly reading minutes, all-time, session-based
    if (typeof books_monthly_time !== 'undefined' && books_monthly_time.length > 0) {
        renderSparkline('sparkline_time', books_monthly_time.map(d => d.minutes));
    }
    // (else: sparkline_time container stays empty per CONTEXT.md locked decision)

    // ── Comparison badges ─────────────────────────────────────────────────

    renderBadge('.js-badge-books');
    renderBadge('.js-badge-chapters');
    renderBadge('.js-badge-time');

    // ── Month label formatter (shared by column chart + pace chart) ────────

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
    ];

    function formatMonth(ym: string): string {
        const parts = ym.split('-');
        if (parts.length < 2) return ym;
        const monthIdx = parseInt(parts[1], 10) - 1;
        const year = parts[0];
        return (monthNames[monthIdx] ?? ym) + ' ' + year;
    }

    // ── Column chart — Books completed per month ──────────────────────────

    if (books_monthly && books_monthly.length > 0) {

    const columnChart = Highcharts.chart('chart_books_monthly', Highcharts.merge(getChartTheme(), {
        chart: { type: 'column', height: 350 },
        title: { text: 'Books Completed per Month', style: { fontSize: '14px' } },
        xAxis: {
            categories: books_monthly.map(d => d.month),
            labels: { rotation: -45 },
        },
        yAxis: {
            title: { text: 'Books' },
            allowDecimals: false,
            gridLineWidth: 0.1,
        },
        tooltip: {
            formatter: function (this: any): string {
                const count = this.y ?? 0;
                const label = formatMonth(this.x);
                return `<b>${label}</b><br/>${count} book${count !== 1 ? 's' : ''} completed`;
            },
        },
        legend: { enabled: false },
        credits: { enabled: false },
        series: [{
            type: 'column',
            name: 'Books',
            color: '#4dabf7',
            data: books_monthly.map(d => d.count),
        }],
    } as any));

    wireRangeButtons('.js-range-books', books_monthly as any[], columnChart, 'month', 'count');

    } // end if (books_monthly && books_monthly.length > 0)

    // ── Line chart — Reading pace (chapters per month) ────────────────────

    if (typeof books_monthly_pace === 'undefined' || books_monthly_pace.length === 0) {
        const rangeGroup = document.querySelector('.js-range-pace') as HTMLElement | null;
        if (rangeGroup) rangeGroup.style.display = 'none';
        renderEmptyState('chart_books_pace', 350);
    } else {
        const paceChart = Highcharts.chart('chart_books_pace', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 350 },
            title: { text: 'Reading Pace (Chapters per Month)', style: { fontSize: '14px' } },
            xAxis: {
                categories: books_monthly_pace.map(d => d.month),
                labels: { rotation: -45 },
                tickAmount: 12,
            },
            yAxis: {
                title: { text: 'Chapters' },
                allowDecimals: false,
                gridLineWidth: 0.1,
            },
            tooltip: {
                formatter: function (this: any): string {
                    const chapters = this.y ?? 0;
                    const label = formatMonth(this.x);
                    return `<b>${label}</b><br/>${chapters} chapter${chapters !== 1 ? 's' : ''}`;
                },
            },
            legend: { enabled: false },
            credits: { enabled: false },
            plotOptions: {
                line: {
                    marker: { enabled: false },
                },
            },
            series: [{
                type: 'line',
                name: 'Chapters',
                color: '#51cf66',
                data: books_monthly_pace.map(d => d.chapters),
            }],
        } as any));

        wireRangeButtons('.js-range-pace', books_monthly_pace as any[], paceChart, 'month', 'chapters');
    }
}

// ── Books list ───────────────────────────────────────────────────────────────

function books() {
    let table = document.querySelector('.datatable[data-table="books_list"]') as HTMLElement;
    if(!table) {
        return;
    }

    let datatable = new DataTable('.datatable[data-table="books_list"]',{
        pageLength: 20,
        lengthMenu: [10,20,50,100],
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getBooks',
            data: function(data) {
                return data;
            }
        },
        columns: [
            { data: null, render: function(data) {
                return `<span class="table-circle" style="background-color:${data.color}"></span> <a href="/books/history/${data.books_id}">${data.name}</a>` + goodreads_link(data.goodreads_id);
            }},
            { data: 'author' },
            { data: 'chapters' },
            { data: 'pages' },
            { data: 'pch' },
            { data: null, render: function(data) {
                return `<div class="progress" style="width:80px"><div class="progress-bar" role="progressbar" style="width: ${data.progress}%" aria-valuenow="${data.progress}" aria-valuemin="0" aria-valuemax="100"></div><div class="progress-bar-text">${data.progress}%</div></div>`;
            }},
            { data: 'time' },
            { data: 'per_chapter' },
            { data: 'ttf' },
            { data: 'days' },
            { data: null, render: function(data) {
                return number_format(data.goodreads_rating,2);
            }},
            { data: null, render: function(data) {
                return number_format(data.goodreads_rating_count,0);
            }},
        ],
        order: [[5,"desc"]] as Order[],
        drawCallback: init_tooltips
    });

    function goodreads_link(id : number) {
        return id !== null ? `&nbsp;<a href="https://www.goodreads.com/book/show/${id}" class="text-decoration-none text-secondary" target="_blank"><sup>[GR]</sup></a>` : '';
    }
}

function reads() {
    let table = document.querySelector('.datatable[data-table="books_reads"]') as HTMLElement;
    if(!table) {
        return;
    }

    let datatable = new DataTable('.datatable[data-table="books_reads"]',{
        pageLength: 20,
        lengthMenu: [10,20,50,100],
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getReads',
            data: function(data : BooksAjaxData) {
                data.books_id = table.getAttribute('data-books_id') ?? '';
                return data;
            }
        },
        columns: [
            { data: null, render: function(data) {
                const pausedBadge = data.is_paused
                    ? `<span class="badge bg-warning text-dark me-1">Paused</span> `
                    : '';
                return `${pausedBadge}<span class="table-circle" style="background-color:${data.color}"></span> ${data.name}`;
            }},
            { data: null, render: function(data) {
                if (data.chapter_tooltip) {
                    const safeTitle = String(data.chapter_tooltip).replace(/"/g, '&quot;');
                    return `<span data-bs-toggle="tooltip" title="${safeTitle}">${data.chapter}</span>`;
                }
                return data.chapter;
            }},
            { data: 'time' },
            { data: null, render: function(data) {
                return `<span data-bs-toggle="tooltip" title="${data.date_end}">${data.date_end_diff}</span>`;
            }},
        ],
        order: [[3,"desc"]],
        createdRow: function(row, data : { [key: string]: any }, dataIndex, cells) {
            let element = row as HTMLElement;
            if(data.is_current) {
                element.classList.add('row-current');
            }
            if(data.is_paused) {
                element.classList.add('table-warning');
            }
        },
        drawCallback: init_tooltips
    });

    let next_books_id : number;
    let next_chapter : number;

    function updateNextRead() {
        get('?do=getNextRead',{
            books_id: table.getAttribute('data-books_id') ?? ''
        },(data : {
            books_id: number,
            chapter: number,
            next: string,
            book_progress: number,
            book: string,
            book_chapters_left: number,
            is_started: boolean,
            is_paused: boolean
        }) => {
            next_books_id = data.books_id;
            next_chapter = data.chapter;

            val('[name="chapter"]',data.chapter);
            let next = document.querySelector('.next-up');
            if(next) {
                next.innerHTML = data.next;
            }
            let book_progress = document.querySelector('.book-progress') as HTMLElement;
            let book_progress_text = book_progress.parentElement?.querySelector('.progress-bar-text') as HTMLElement;
            book_progress.style.width = data.book_progress + '%';
            book_progress_text.innerHTML = data.book + " (" + data.book_progress + '% - ' + data.book_chapters_left + ' Chapters Left)';

            let startReadEl = document.querySelector('.js-start-read') as HTMLElement;
            let endReadEl = document.querySelector('.js-end-read') as HTMLElement;
            const pauseReadEl = document.querySelector('.js-pause-read') as HTMLElement;
            const resumeReadEl = document.querySelector('.js-resume-read') as HTMLElement;

            if(data.is_paused) {
                resumeReadEl.style.display = '';
                endReadEl.style.display = 'none';
                startReadEl.style.display = 'none';
                pauseReadEl.style.display = 'none';
            } else if(data.is_started) {
                endReadEl.style.display = '';
                pauseReadEl.style.display = '';
                startReadEl.style.display = 'none';
                resumeReadEl.style.display = 'none';
            } else {
                startReadEl.style.display = '';
                endReadEl.style.display = 'none';
                pauseReadEl.style.display = 'none';
                resumeReadEl.style.display = 'none';
            }
        });
    }

    updateNextRead();
    //set_time();
    //setInterval(set_time,1000);

    on('click','.js-start-read',(start_read : HTMLElement) => {
        let end_read = document.querySelector('.js-end-read') as HTMLElement;
        const pause_read = document.querySelector('.js-pause-read') as HTMLElement;
        const resume_read = document.querySelector('.js-resume-read') as HTMLElement;
        start_read.classList.add('disabled');

        if(next_books_id && next_chapter) {
            get('?do=startRead',{
                next_books_id: next_books_id,
                chapter: next_chapter
            },(data : any) => {
                if(data) {
                    start_read.classList.remove('disabled');
                    start_read.style.display = 'none';
                    end_read.style.display = '';
                    pause_read.style.display = '';
                    resume_read.style.display = 'none';
                    datatable.ajax.reload();
                }
            });
        }
    });

    on('click','.js-end-read',(end_read : HTMLElement) => {
        let start_read = document.querySelector('.js-start-read') as HTMLElement;
        const pause_read = document.querySelector('.js-pause-read') as HTMLElement;
        const resume_read = document.querySelector('.js-resume-read') as HTMLElement;
        end_read.classList.add('disabled');

        if(next_books_id && next_chapter) {
            get('?do=endRead',{
                next_books_id: next_books_id,
                chapter: next_chapter
            },(data : any) => {
                if(data) {
                    end_read.classList.remove('disabled');
                    end_read.style.display = 'none';
                    pause_read.style.display = 'none';
                    resume_read.style.display = 'none';
                    start_read.style.display = '';
                    datatable.ajax.reload();
                    updateNextRead();
                }
            });
        }
    });

    on('click', '.js-pause-read', (pause_read: HTMLElement) => {
        const resume_read = document.querySelector('.js-resume-read') as HTMLElement;
        const end_read = document.querySelector('.js-end-read') as HTMLElement;
        pause_read.classList.add('disabled');
        if (next_books_id && next_chapter) {
            get('?do=pauseRead', {
                next_books_id: next_books_id,
                chapter: next_chapter
            }, (data: any) => {
                pause_read.classList.remove('disabled');
                if (data && data.status === 1) {
                    pause_read.style.display = 'none';
                    end_read.style.display = 'none';
                    resume_read.style.display = '';
                    datatable.ajax.reload();
                    updateNextRead();
                } else {
                    notifier.alert('Failed to pause read');
                }
            });
        }
    });

    on('click', '.js-resume-read', (resume_read: HTMLElement) => {
        const pause_read = document.querySelector('.js-pause-read') as HTMLElement;
        const end_read = document.querySelector('.js-end-read') as HTMLElement;
        resume_read.classList.add('disabled');
        if (next_books_id && next_chapter) {
            get('?do=resumeRead', {
                next_books_id: next_books_id,
                chapter: next_chapter
            }, (data: any) => {
                resume_read.classList.remove('disabled');
                if (data && data.status === 1) {
                    resume_read.style.display = 'none';
                    pause_read.style.display = '';
                    end_read.style.display = '';
                    datatable.ajax.reload();
                    updateNextRead();
                } else {
                    notifier.alert('Failed to resume read');
                }
            });
        }
    });
}
